/**
 * ‼ נתוני הדגמה בלבד — שלב 1 ‼
 *
 * כל הנתונים כאן סינתטיים. אין אתר אמיתי, אין אדם אמיתי, אין חברה
 * אמיתית, ואין נתון שיובא מדוח הייחוס (C-09). שמות האנשים והחברות
 * נבחרו כך שייראו בבירור כדוגמה.
 *
 * ספריית השאלות היא 12 שאלות ההדגמה מ־REGULATORY-MATRIX §7. **אף שאלה
 * אינה במצב ״התאמה מקצועית נבדקה״** (A-17), והמצב הזה מוצג בממשק ובדוח.
 */

import type {
  Answer,
  Area,
  BrandingVersion,
  DocumentVersion,
  Evidence,
  Finding,
  PlanRequirement,
  QuestionVersion,
  Site,
  StagePeriod,
  Visit,
} from './types.ts';

export const DEMO_NOTICE = 'נתוני הדגמה — שלב 1. אין זה דוח בטיחות תקף ואין להסתמך עליו בשטח.';

export const DEMO_BRANDING: BrandingVersion = {
  version: 'brand-2026.09',
  organizationName: 'חכשורי בטיחות',
  productName: 'Hakshuri Safety',
  colors: { ink: '#17324D', accent: '#087F8C', surface: '#F4F7FA' },
  footerText: 'הופק במערכת חכשורי בטיחות · נתוני הדגמה',
};

export const DEMO_SITE: Site = {
  id: 'site-demo-0001',
  name: 'אתר הדגמה — מתחם דוגמה צפון',
  address: 'רחוב הדוגמה 12, עיר לדוגמה',
  permitAreaSqm: 18_400,
  clientName: 'חברת מזמין לדוגמה בע״מ',
  contractorName: 'קבלן ביצוע לדוגמה בע״מ',
  hasSiteManager: true,
  requiresSection192Notice: true,
};

export const DEMO_AREAS: Area[] = [
  { id: 'area-demo-01', siteId: DEMO_SITE.id, name: 'מבנה A — שלד' },
  { id: 'area-demo-02', siteId: DEMO_SITE.id, name: 'מבנה B — גמרים' },
  { id: 'area-demo-03', siteId: DEMO_SITE.id, name: 'חצר ודרכי גישה' },
];

export const DEMO_STAGE_PERIODS: StagePeriod[] = [
  {
    id: 'stage-demo-01',
    areaId: 'area-demo-01',
    stageName: 'שלד',
    from: '2026-06-01T00:00:00.000Z',
    to: null,
  },
  {
    id: 'stage-demo-02',
    areaId: 'area-demo-02',
    stageName: 'גמרים',
    from: '2026-08-15T00:00:00.000Z',
    to: null,
  },
  {
    id: 'stage-demo-03',
    areaId: 'area-demo-03',
    stageName: 'עבודות עפר',
    from: '2026-05-01T00:00:00.000Z',
    to: '2026-09-01T00:00:00.000Z',
  },
];

export const DEMO_PLAN_DOCUMENT: DocumentVersion = {
  id: 'docver-demo-01',
  documentId: 'doc-demo-01',
  siteId: DEMO_SITE.id,
  title: 'תוכנית לניהול הבטיחות — אתר הדגמה',
  versionLabel: 'גרסה 3',
  uploadedAt: '2026-09-02T07:30:00.000Z',
  pageCount: 4,
  hasTextLayer: true,
  demoFilePath: 'demo-plan.pdf',
};

/** אותה תוכנית כסריקה ללא שכבת טקסט — תרחיש הקבלה של RQ-C04. */
export const DEMO_PLAN_DOCUMENT_SCANNED: DocumentVersion = {
  id: 'docver-demo-02',
  documentId: 'doc-demo-02',
  siteId: DEMO_SITE.id,
  title: 'תוכנית לניהול הבטיחות — עותק סרוק (ללא שכבת טקסט)',
  versionLabel: 'גרסה 3 — סריקה',
  uploadedAt: '2026-09-02T07:45:00.000Z',
  pageCount: 4,
  hasTextLayer: false,
  demoFilePath: 'demo-plan-scanned.pdf',
};

export const DEMO_PLAN_REQUIREMENTS: PlanRequirement[] = [
  {
    id: 'planreq-demo-01',
    documentVersionId: DEMO_PLAN_DOCUMENT.id,
    page: 2,
    riskText: 'נפילה מגובה בעבודות שלד',
    responseText: 'מעקה תקני היקפי בכל קומה פעילה, ובדיקת שלמותו לפני תחילת משמרת',
    linkedBy: 'ישראלה בקרת (בקרית הדגמה)',
    linkedAt: '2026-09-03T06:10:00.000Z',
  },
  {
    id: 'planreq-demo-02',
    documentVersionId: DEMO_PLAN_DOCUMENT.id,
    page: 3,
    riskText: 'התחשמלות מכבל מאריך פגום',
    responseText: 'לוחות חשמל נעולים, ממסר פחת נבדק, כבלים מוגבהים מחוץ לשטח תנועה',
    linkedBy: 'ישראלה בקרת (בקרית הדגמה)',
    linkedAt: '2026-09-03T06:14:00.000Z',
  },
];

/**
 * 12 שאלות ההדגמה. המקורות והסטטוסים מועתקים מ־REGULATORY-MATRIX §7
 * כפי שהם — אין כאן הוספה של מגבלה מספרית שלא אומתה.
 */
export const DEMO_QUESTIONS: QuestionVersion[] = [
  {
    id: 'qv-Q-01',
    code: 'Q-01',
    text: 'איזו גרסת תוכנית בטיחות נבדקת, ומה מועד עדכונה האחרון?',
    sourceLayer: 'דין',
    status: 'נוסח אומת',
    legal: {
      ruleId: 'R-12',
      section: '2(א)(3)',
      sourceName: 'ק״ת 12061',
      printedPage: '51',
      status: 'נוסח אומת',
    },
    triggerText: 'כל ביקור',
  },
  {
    id: 'qv-Q-02',
    code: 'Q-02',
    text: 'האם התוכנית סווגה נכון — תוכנית לניהול הבטיחות (50+) או תוכנית ארגון בטיחות (עד 49)?',
    sourceLayer: 'דין',
    status: 'נוסח אומת',
    legal: {
      ruleId: 'R-02',
      section: '2(א)',
      sourceName: 'ק״ת 12061',
      printedPage: '50',
      status: 'נוסח אומת',
    },
    triggerText: 'כל ביקור',
  },
  {
    id: 'qv-Q-03',
    code: 'Q-03',
    text: 'האם העתק תוכנית הבטיחות נמצא באתר ומצורף לפנקס הכללי?',
    sourceLayer: 'דין',
    status: 'נוסח אומת',
    legal: {
      ruleId: 'R-10',
      section: '2(ב)(1)',
      sourceName: 'ק״ת 12061',
      printedPage: '51',
      status: 'נוסח אומת',
    },
    triggerText: 'כל ביקור',
  },
  {
    id: 'qv-Q-04',
    code: 'Q-04',
    text: 'מי מונה כבקר בטיחות, וממתי? האם קיים ניגוד תפקידים באותו אתר?',
    sourceLayer: 'דין',
    status: 'נוסח אומת',
    legal: {
      ruleId: 'R-03',
      section: '2א(א)',
      sourceName: 'ק״ת 12061',
      printedPage: '51',
      status: 'נוסח אומת',
    },
    triggerText: 'כל ביקור',
  },
  {
    id: 'qv-Q-05',
    code: 'Q-05',
    text: 'באתר של 15,000 מ״ר ומעלה — האם מונה מנהל אתר?',
    sourceLayer: 'דין',
    status: 'נוסח אומת',
    legal: {
      ruleId: 'R-12',
      section: '2(א)(2)',
      sourceName: 'ק״ת 12061',
      printedPage: '51',
      status: 'נוסח אומת',
    },
    triggerText: 'שטח היתר 15,000 מ״ר ומעלה',
  },
  {
    id: 'qv-Q-06',
    code: 'Q-06',
    text: 'אילו מענים לסיכוני התוכנית נבדקו היום, ומה נצפה בפועל?',
    sourceLayer: 'דין',
    status: 'נוסח אומת',
    legal: {
      ruleId: 'R-05',
      section: '2א(ב)(2)',
      sourceName: 'ק״ת 12061',
      printedPage: '51',
      status: 'נוסח אומת',
    },
    triggerText: 'כל ביקור',
  },
  {
    id: 'qv-Q-07',
    code: 'Q-07',
    text: 'האם סופקו אמצעי מיגון מתאימים לעובדים למשימות שנצפו?',
    sourceLayer: 'דין',
    status: 'נוסח אומת',
    legal: {
      ruleId: 'R-05',
      section: '2א(ב)(2)',
      sourceName: 'ק״ת 12061',
      printedPage: '51',
      status: 'נוסח אומת',
    },
    triggerText: 'כל ביקור',
  },
  {
    id: 'qv-Q-08',
    code: 'Q-08',
    text: 'האם קיימות הדרכות הנוגעות לסיכונים הקשורים לעבודה שנצפתה?',
    sourceLayer: 'דין',
    status: 'נוסח אומת',
    legal: {
      ruleId: 'R-05',
      section: '2א(ב)(2)',
      sourceName: 'ק״ת 12061',
      printedPage: '51',
      status: 'נוסח אומת',
    },
    triggerText: 'כל ביקור',
  },
  {
    id: 'qv-Q-09',
    code: 'Q-09',
    text: 'האם ההדרכה שתועדה בוצעה פנים אל פנים?',
    sourceLayer: 'דין',
    status: 'נוסח אומת',
    legal: {
      ruleId: 'T-03',
      section: '3א(א)',
      sourceName: 'ק״ת 12471',
      printedPage: '2404',
      status: 'נוסח אומת',
    },
    triggerText: 'אתר המחייב הודעה לפי סעיף 192',
  },
  {
    id: 'qv-Q-10',
    code: 'Q-10',
    text: 'האם הוצגו דוחות הבקרה של מנהל האתר (כל 30 ימים) על יישום המענים ועל ההדרכות?',
    sourceLayer: 'דין',
    status: 'נוסח אומת',
    legal: {
      ruleId: 'R-14',
      section: '2ב(ב)(2),(4)',
      sourceName: 'ק״ת 12061',
      printedPage: '52',
      status: 'נוסח אומת',
    },
    triggerText: 'קיים מנהל אתר',
  },
  {
    id: 'qv-Q-11',
    code: 'Q-11',
    text: 'מה מצב הממצאים מהביקור הקודם — דווח תיקון, התקבלה ראיה, או אומת?',
    sourceLayer: 'תוכנית',
    status: 'לא מאומת כחובה',
    legal: null,
    triggerText: 'קיימים ממצאים פתוחים',
  },
  {
    id: 'qv-Q-12',
    code: 'Q-12',
    text: 'אילו אזורים לא היו נגישים היום, ומה מגבלות הבדיקה?',
    sourceLayer: 'מקצועי',
    status: 'לא מאומת כחובה',
    legal: null,
    triggerText: 'כל ביקור',
  },
];

export const DEMO_VISIT: Visit = {
  id: 'visit-demo-0001',
  siteId: DEMO_SITE.id,
  inspectorName: 'ישראלה בקרת',
  inspectorLicenseText: 'בקרית בטיחות לדוגמה · מס׳ הסמכה DEMO-0000',
  observedAt: '2026-09-21T06:15:00.000Z',
  observedAtSource: 'device',
  durationMinutes: 135,
  status: 'טיוטה',
  areaIds: ['area-demo-01', 'area-demo-02', 'area-demo-03'],
  stagePeriodIds: ['stage-demo-01', 'stage-demo-02'],
  planDocumentVersionId: DEMO_PLAN_DOCUMENT.id,
  checklistTemplateVersion: 'demo-12/2026.09',
  limitations: [
    { id: 'lim-01', text: 'מבנה B קומה 4 לא היה נגיש בשל עבודות יציקה פעילות' },
    { id: 'lim-02', text: 'לא הוצגו תיקי הדרכה של קבלן משנה לעבודות חשמל' },
  ],
  rowVersion: 7,
};

function flags(partial: Partial<Finding['flags']> = {}): Finding['flags'] {
  return {
    assignedTo: null,
    fixReportedBy: null,
    fixReportedAt: null,
    hasEvidence: false,
    officialApproval: false,
    inspectorVerified: null,
    reopenedFromEventId: null,
    ...partial,
  };
}

/** הממצא הארוך — תרחיש הקבלה RQ-I04 (ממצא ארוך מעמוד). */
const LONG_FINDING_TEXT = [
  'בסיור בקומה 3 במבנה A נצפה קטע מעקה היקפי באורך של כ־12 מטרים שבו חסר אזן תיכון, ובשני מקטעים נוספים האזן העליון אינו מחובר לעמוד אלא נשען עליו בלבד.',
  'המעקה נמצא בקצה הצפוני של הקומה, בסמוך לפתח מדרגות פעיל שדרכו עוברים עובדים אל קומה 4. בשעת הביקור עבדו במקטע זה שני עובדים בעבודות טפסנות, ללא ריתום ובלי נקודת עיגון זמינה בטווח.',
  'המעקה מופיע בתוכנית הבטיחות של האתר, בעמוד 2, כמענה לסיכון ״נפילה מגובה בעבודות שלד״. כלומר מדובר באי־יישום של מענה שנקבע בתוכנית, ולא בהיעדר התייחסות בתוכנית.',
  'בבדיקה מול מנהל העבודה נמסר כי המקטע פורק אתמול לצורך העלאת חומר במנוף ולא הוחזר בתום הפעולה. לא הוצג נוהל כתוב לפירוק זמני והחזרה של מעקה, ולא נמצא תיעוד של בדיקת שלמות מעקים לפני תחילת המשמרת.',
  'בנוסף נצפה כי לוח הסימון שהוצב באזור אינו חוסם את הגישה בפועל, וניתן לעבור לצד האסור בלי להזיז דבר. הסימון אינו תחליף לחסימה פיזית.',
  'מכיוון שמדובר בעבודה פעילה בגובה בקצה לא מגודר, בנוכחות עובדים ובלי אמצעי ריתום זמין, הסיכון הוערך על ידי הבקר כבלתי קביל, והועברה התראה מיידית בכתב במקביל לדוח זה, בהתאם לתקנה 2א(ג). ההתראה נשלחה בשעה 09:40 לארבעת הנמענים, ואישור המזמין טרם התקבל במועד סגירת דוח זה.',
].join('\n\n');

export const DEMO_FINDINGS: Finding[] = [
  {
    id: 'finding-demo-01',
    visitId: DEMO_VISIT.id,
    findingNumber: 1,
    description: LONG_FINDING_TEXT,
    locationText: 'מבנה A — קומה 3, קצה צפוני',
    risk: 'בלתי קביל',
    inspectorDecision: 'הפסקת עבודה במקטע עד להשלמת מעקה תקני; נדרשת התראה מיידית לפי תקנה 2א(ג).',
    requiredAction: 'השלמת אזן תיכון וחיבור אזן עליון לעמודים; חסימה פיזית של הגישה עד להשלמה.',
    dueText: 'מיידי',
    flags: flags({ assignedTo: 'מנהל עבודה — דוגמן לדוגמה', hasEvidence: true }),
    legal: {
      ruleId: 'R-07',
      section: '2א(ג)',
      sourceName: 'ק״ת 12061',
      printedPage: '51',
      status: 'נוסח אומת',
    },
    sourceQuestionCode: 'Q-06',
  },
  {
    id: 'finding-demo-02',
    visitId: DEMO_VISIT.id,
    findingNumber: 2,
    description:
      'כבל מאריך בחצר עובר על דרך תנועת כלים, עטיפתו החיצונית קרועה בנקודה אחת והוא אינו מוגבה. הכבל מזין מסור בקומה 1.',
    locationText: 'חצר ודרכי גישה — ליד שער כניסה',
    risk: 'גבוה',
    inspectorDecision: 'ניתוק הכבל מהמתח עד להחלפה; אין לאפשר שימוש בכבל פגום.',
    requiredAction: 'החלפת הכבל והגבהתו מחוץ לשטח תנועה; בדיקת ממסר פחת ותיעודה.',
    dueText: 'עד 48 שעות',
    flags: flags({
      assignedTo: 'חשמלאי האתר — דוגמן לדוגמה',
      fixReportedBy: 'מנהל האתר — דוגמן לדוגמה',
      fixReportedAt: '2026-09-21T13:20:00.000Z',
      hasEvidence: true,
    }),
    legal: {
      ruleId: 'R-05',
      section: '2א(ב)(2)',
      sourceName: 'ק״ת 12061',
      printedPage: '51',
      status: 'נוסח אומת',
    },
    sourceQuestionCode: 'Q-07',
  },
  {
    id: 'finding-demo-03',
    visitId: DEMO_VISIT.id,
    findingNumber: 3,
    description:
      'לא הוצגו דוחות בקרה של מנהל האתר עבור החודשיים האחרונים. נמסר בעל פה שהבקרות בוצעו, אך לא הוצג תיעוד.',
    locationText: 'משרד האתר',
    risk: 'בינוני',
    inspectorDecision: 'דיווח בעל פה אינו תחליף לתיעוד; נדרשת הצגת הדוחות.',
    requiredAction: 'הצגת דוחות הבקרה של מנהל האתר, לרבות יישום מענים והדרכות.',
    dueText: 'עד הביקור הבא',
    flags: flags({ assignedTo: 'מנהל האתר — דוגמן לדוגמה' }),
    legal: {
      ruleId: 'R-14',
      section: '2ב(ב)(2),(4)',
      sourceName: 'ק״ת 12061',
      printedPage: '52',
      status: 'נוסח אומת',
    },
    sourceQuestionCode: 'Q-10',
  },
];

/** ממצאים מביקור קודם — מזינים את פרק המעקב ואת Q-11. */
export const DEMO_PREVIOUS_FINDINGS: Finding[] = [
  {
    id: 'finding-prev-01',
    visitId: 'visit-demo-0000',
    findingNumber: 14,
    description: 'פתח ריצפתי במבנה B ללא כיסוי מקובע.',
    locationText: 'מבנה B — קומה 2',
    risk: 'גבוה',
    inspectorDecision: 'כיסוי מקובע ומסומן, או גידור היקפי.',
    requiredAction: 'קיבוע כיסוי וסימונו.',
    dueText: 'בוצע',
    flags: flags({
      assignedTo: 'מנהל עבודה — דוגמן לדוגמה',
      fixReportedBy: 'מנהל עבודה — דוגמן לדוגמה',
      fixReportedAt: '2026-09-08T11:00:00.000Z',
      hasEvidence: true,
      inspectorVerified: 'תקין',
    }),
    legal: null,
    sourceQuestionCode: null,
  },
  {
    id: 'finding-prev-02',
    visitId: 'visit-demo-0000',
    findingNumber: 15,
    description: 'סולם נייד ללא קשירה בראשו, בשימוש לעלייה לפיגום.',
    locationText: 'מבנה A — קומה 1',
    risk: 'גבוה',
    inspectorDecision: 'קשירת הסולם או החלפתו בגישה תקנית.',
    requiredAction: 'קשירה בראש הסולם; הדרכת העובדים.',
    dueText: 'עבר המועד',
    flags: flags({
      assignedTo: 'מנהל עבודה — דוגמן לדוגמה',
      fixReportedBy: 'מנהל עבודה — דוגמן לדוגמה',
      fixReportedAt: '2026-09-09T08:30:00.000Z',
      hasEvidence: false,
    }),
    legal: null,
    sourceQuestionCode: null,
  },
];

export function buildDemoAnswers(visitId: string): Answer[] {
  const byCode: Record<string, { state: Answer['state']; note: string }> = {
    'Q-01': { state: 'תקין', note: 'נבדקה גרסה 3, עודכנה ב־02.09.2026.' },
    'Q-02': { state: 'תקין', note: 'סווגה כתוכנית לניהול הבטיחות; באתר מעל 50 עובדים.' },
    'Q-03': { state: 'תקין', note: 'העתק נמצא במשרד האתר ומצורף לפנקס הכללי.' },
    'Q-04': { state: 'תקין', note: 'מינוי מ־01.06.2026. לא נמצא ניגוד תפקידים.' },
    'Q-05': { state: 'תקין', note: 'שטח היתר 18,400 מ״ר; מנהל אתר מונה.' },
    'Q-06': { state: 'ליקוי', note: 'מענה המעקה שבתוכנית (עמוד 2) אינו מיושם בקומה 3 במבנה A.' },
    'Q-07': { state: 'ליקוי', note: 'לא הוצגו אמצעי ריתום זמינים לעובדים בקצה לא מגודר.' },
    'Q-08': { state: 'לא נבדק', note: 'תיקי ההדרכה של קבלן המשנה לחשמל לא הוצגו בביקור.' },
    'Q-09': { state: 'לא נבדק', note: 'לא הוצג תיעוד שניתן היה לבדוק ממנו את אופן ההדרכה.' },
    'Q-10': { state: 'ליקוי', note: 'דוחות הבקרה של מנהל האתר לא הוצגו.' },
    'Q-11': { state: 'תקין', note: 'ממצא 14 אומת כתקין; ממצא 15 דווח כתוקן אך טרם אומת.' },
    'Q-12': { state: 'ליקוי', note: 'מבנה B קומה 4 לא היה נגיש; ראו מגבלות הביקור.' },
  };

  return DEMO_QUESTIONS.map((question, index) => {
    const entry = byCode[question.code];
    return {
      id: `answer-demo-${String(index + 1).padStart(2, '0')}`,
      visitId,
      questionVersionId: question.id,
      state: entry?.state ?? null,
      note: entry?.note ?? '',
      evidenceIds: [],
      updatedAt: '2026-09-21T08:40:00.000Z',
      rowVersion: 1,
    };
  });
}

export const DEMO_EVIDENCE_CAPTIONS: ReadonlyArray<{
  caption: string;
  phase: Evidence['phase'];
  findingId: string | null;
}> = [
  { caption: 'מעקה חסר אזן תיכון — מבט מצפון', phase: 'לפני', findingId: 'finding-demo-01' },
  { caption: 'מעקה חסר אזן תיכון — מבט מקרוב', phase: 'לפני', findingId: 'finding-demo-01' },
  { caption: 'פתח מדרגות פעיל בסמוך למקטע', phase: 'אחר', findingId: 'finding-demo-01' },
  { caption: 'כבל מאריך קרוע על דרך תנועה', phase: 'לפני', findingId: 'finding-demo-02' },
  { caption: 'כבל שהוחלף והוגבה', phase: 'אחרי', findingId: 'finding-demo-02' },
  { caption: 'לוח חשמל ראשי — מבט כללי', phase: 'אחר', findingId: 'finding-demo-02' },
  { caption: 'משרד האתר — תיקיית מסמכים שהוצגה', phase: 'אחר', findingId: 'finding-demo-03' },
  { caption: 'שילוט הכוונה בחצר', phase: 'אחר', findingId: null },
];
